import { useEffect, useState, type ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Gauge, Key, Loader2, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { api, ApiError } from '../lib/api.js';
import { useAuth } from '../contexts/auth.js';
import { useOrgContext } from '../contexts/org.js';
import { Badge, GlassButton, GlassCard, GradientText, PageHeader, ConfirmModal } from '../ui/index.js';

interface ApiKey {
  key_id: string;
  user_id: string;
  org_id: string;
  product_id: string | null;
  name: string;
  scopes: string[];
  rate_limit_override: Record<string, unknown> | null;
  status: string;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
}

interface RateLimits {
  requestsPerMinute: number;
  requestsPerHour: number;
  tokensPerDay: number;
  costPerDayUsd: number;
}

/** Null where the override inherits the default. */
interface RateLimitOverride {
  orgId: string;
  productId: string | null;
  requestsPerMinute: number | null;
  requestsPerHour: number | null;
  tokensPerDay: number | null;
  costPerDayUsd: number | null;
}

/** A number as a person types it: `10,000,000` and `50 000` are numbers too. */
function toNumber(text: string): number {
  return Number(text.replace(/[,\s_]/g, ''));
}

interface Notice { type: 'success' | 'error' | 'info'; message: string; }

/** The Issue Key form, as typed. */
interface NewKeyDraft { name: string; userId: string; orgId: string; scopes: string; expires: string; }

const DEFAULT_SCOPES = 'chat:completions, models:read';
const EMPTY_KEY: NewKeyDraft = { name: '', userId: '', orgId: '', scopes: DEFAULT_SCOPES, expires: '' };

function noticeClass(type: Notice['type']): string {
  switch (type) {
    case 'success': return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300';
    case 'error': return 'border-red-500/30 bg-red-500/10 text-red-300';
    default: return 'border-white/10 bg-white/5 text-white/70';
  }
}

export function PlatformApiKeysPage() {
  const { claims } = useAuth();
  const { selectedOrgId } = useOrgContext();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  /** Rule42's build and run keys live now. They come and go with their runs, so they are counted, not listed. */
  const [liveBuildKeys, setLiveBuildKeys] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState<NewKeyDraft>(EMPTY_KEY);
  const [creating, setCreating] = useState(false);
  /** Why the last Create Key failed, shown in the form, which stays open to fix it. */
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [defaults, setDefaults] = useState<RateLimits | null>(null);
  const [overrides, setOverrides] = useState<RateLimitOverride[]>([]);
  const [draft, setDraft] = useState({ orgId: '', productId: '', requestsPerMinute: '', requestsPerHour: '', tokensPerDay: '', costPerDayUsd: '' });
  /** The defaults as they are being edited; null while they are only shown. */
  const [defaultsDraft, setDefaultsDraft] = useState<Record<keyof RateLimits, string> | null>(null);

  async function load() {
    try {
      setLoading(true);
      const [result, limits] = await Promise.all([
        api.get<{ keys: ApiKey[]; liveBuildKeys?: number }>('/api/v1/platform/api-keys'),
        api.get<{ defaults: RateLimits; overrides: RateLimitOverride[] }>('/api/v1/platform/rate-limits').catch(() => null),
      ]);
      setKeys(result.keys);
      setLiveBuildKeys(result.liveBuildKeys ?? 0);
      if (limits) {
        setDefaults(limits.defaults);
        setOverrides(limits.overrides);
      }
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof ApiError ? error.message : 'Failed to load API keys.' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  /** Opens the form, filling in the signed-in person and organisation where nothing is typed yet. */
  function openCreate() {
    setNewKey((draft) => ({
      ...draft,
      userId: draft.userId || claims?.sub || '',
      orgId: draft.orgId || selectedOrgId || claims?.org_id || '',
    }));
    setCreateError(null);
    setShowCreate(true);
  }

  async function createKey() {
    const scopes = newKey.scopes.split(',').map((s) => s.trim()).filter(Boolean);
    if (!newKey.name.trim() || !newKey.userId.trim() || !newKey.orgId.trim()) {
      setCreateError('A name, a user ID and an organisation ID are required.');
      return;
    }
    if (scopes.length === 0) {
      setCreateError('A key needs at least one scope, such as chat:completions.');
      return;
    }
    try {
      setCreating(true);
      setCreateError(null);
      const result = await api.post<{ secret: string; keyId: string }>('/api/v1/platform/api-keys', {
        name: newKey.name.trim(),
        userId: newKey.userId.trim(),
        orgId: newKey.orgId.trim(),
        scopes,
        // The end of the chosen day, where the admin is.
        expiresAt: newKey.expires ? new Date(`${newKey.expires}T23:59:59`).toISOString() : undefined,
      });
      setCreatedKey(result.secret);
      setShowCreate(false);
      setNewKey(EMPTY_KEY);
      setNotice({ type: 'success', message: 'API key created. Save the secret — it won\'t be shown again.' });
      void load();
    } catch (error) {
      setCreateError(error instanceof ApiError ? error.message : 'Failed to create key.');
    } finally {
      setCreating(false);
    }
  }

  async function revokeKey(keyId: string) {
    try {
      await api.delete(`/api/v1/platform/api-keys/${keyId}`);
      setNotice({ type: 'success', message: 'Key revoked.' });
      void load();
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof ApiError ? error.message : 'Failed to revoke key.' });
    } finally {
      setRevokingId(null);
    }
  }

  async function saveOverride() {
    if (!draft.orgId.trim()) {
      setNotice({ type: 'error', message: 'An organisation ID is required.' });
      return;
    }
    try {
      await api.put('/api/v1/platform/rate-limits', {
        orgId: draft.orgId.trim(),
        productId: draft.productId.trim() || null,
        // Blank means "use the default", so send undefined rather than 0.
        requestsPerMinute: draft.requestsPerMinute ? toNumber(draft.requestsPerMinute) : undefined,
        requestsPerHour: draft.requestsPerHour ? toNumber(draft.requestsPerHour) : undefined,
        tokensPerDay: draft.tokensPerDay ? toNumber(draft.tokensPerDay) : undefined,
        costPerDayUsd: draft.costPerDayUsd ? toNumber(draft.costPerDayUsd) : undefined,
      });
      setDraft({ orgId: '', productId: '', requestsPerMinute: '', requestsPerHour: '', tokensPerDay: '', costPerDayUsd: '' });
      setNotice({ type: 'success', message: 'Rate limit saved.' });
      void load();
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof ApiError ? error.message : 'Failed to save the rate limit.' });
    }
  }

  async function saveDefaults() {
    if (!defaultsDraft) return;
    try {
      await api.put('/api/v1/platform/rate-limits/defaults', {
        requestsPerMinute: toNumber(defaultsDraft.requestsPerMinute),
        requestsPerHour: toNumber(defaultsDraft.requestsPerHour),
        tokensPerDay: toNumber(defaultsDraft.tokensPerDay),
        costPerDayUsd: toNumber(defaultsDraft.costPerDayUsd),
      });
      setDefaultsDraft(null);
      setNotice({ type: 'success', message: 'Default rate limits saved. They apply from the next request.' });
      void load();
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof ApiError ? error.message : 'Failed to save the default rate limits.' });
    }
  }

  /** Puts an override into the form below, where saving replaces it. */
  function editOverride(row: RateLimitOverride) {
    const text = (value: number | null) => (value === null ? '' : String(value));
    setDraft({
      orgId: row.orgId,
      productId: row.productId ?? '',
      requestsPerMinute: text(row.requestsPerMinute),
      requestsPerHour: text(row.requestsPerHour),
      tokensPerDay: text(row.tokensPerDay),
      costPerDayUsd: text(row.costPerDayUsd),
    });
  }

  async function removeOverride(orgId: string, productId: string | null) {
    try {
      await api.delete(`/api/v1/platform/rate-limits/${encodeURIComponent(orgId)}?productId=${encodeURIComponent(productId ?? '')}`);
      setNotice({ type: 'success', message: 'Rate limit removed — the org falls back to the defaults.' });
      void load();
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof ApiError ? error.message : 'Failed to remove the rate limit.' });
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-indigo-400" /></div>;
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={<GradientText>API Keys</GradientText>}
        subtitle="Issue API keys for 3rd party products. Every key is tied to a registered rule42 user and charges their org."
        action={
          <div className="flex items-center gap-2">
            <GlassButton variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void load()}>Refresh</GlassButton>
            <GlassButton variant="primary" icon={<Plus className="h-4 w-4" />} onClick={openCreate}>Issue Key</GlassButton>
          </div>
        }
      />

      {notice && (
        <div className={`flex items-center gap-2 rounded-lg border px-3 py-3 text-sm ${noticeClass(notice.type)}`}>
          {notice.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <span>{notice.message}</span>
        </div>
      )}

      {createdKey && (
        <GlassCard className="space-y-3 border-amber-500/30 bg-amber-500/5">
          <div className="flex items-center gap-2">
            <Key className="h-4 w-4 text-amber-300" />
            <h3 className="text-sm font-semibold text-white">New API Key — Save Now</h3>
          </div>
          <p className="text-xs text-white/50">This key will not be shown again. Copy it now.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-lg bg-black/40 px-3 py-2 text-sm text-amber-200 font-mono break-all">{createdKey}</code>
            <GlassButton variant="secondary" onClick={() => navigator.clipboard.writeText(createdKey)}>Copy</GlassButton>
            <GlassButton variant="ghost" onClick={() => setCreatedKey(null)}>Dismiss</GlassButton>
          </div>
        </GlassCard>
      )}

      <GlassCard className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-white/40">
              <th className="pb-2 pr-4">Name</th>
              <th className="pb-2 pr-4">Key ID</th>
              <th className="pb-2 pr-4">User</th>
              <th className="pb-2 pr-4">Org</th>
              <th className="pb-2 pr-4">Scopes</th>
              <th className="pb-2 pr-4">Status</th>
              <th className="pb-2 pr-4">Last Used</th>
              <th className="pb-2"></th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 ? (
              <tr><td colSpan={8} className="py-8 text-center text-white/40">No API keys issued yet.</td></tr>
            ) : keys.map((key) => (
              <tr key={key.key_id} className="border-b border-white/5">
                <td className="py-2 pr-4 text-white/85">{key.name}</td>
                <td className="py-2 pr-4 text-white/50 font-mono text-xs">{key.key_id}</td>
                <td className="py-2 pr-4 text-white/50 font-mono text-xs">{key.user_id.slice(0, 8)}...</td>
                <td className="py-2 pr-4 text-white/50 font-mono text-xs">{key.org_id.slice(0, 8)}...</td>
                <td className="py-2 pr-4">
                  <div className="flex flex-wrap gap-1">
                    {key.scopes.map((scope) => <Badge key={scope} variant="default">{scope}</Badge>)}
                  </div>
                </td>
                <td className="py-2 pr-4"><Badge variant={key.status === 'active' ? 'success' : 'error'}>{key.status}</Badge></td>
                <td className="py-2 pr-4 text-white/40 text-xs">{key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}</td>
                <td className="py-2">
                  {key.status === 'active' && (
                    <GlassButton variant="danger" size="sm" icon={<Trash2 className="h-3 w-3" />} onClick={() => setRevokingId(key.key_id)}>Revoke</GlassButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {liveBuildKeys > 0 && (
          <p className="border-t border-white/5 px-4 py-3 text-xs text-white/40">
            Not listed: {liveBuildKeys} key{liveBuildKeys === 1 ? '' : 's'} Rule42 holds for index builds and report runs in progress.
            Each is deleted when its run ends.
          </p>
        )}
      </GlassCard>

      {/* Rate limits — enforced on every /v1 call, before anything is forwarded upstream. */}
      <GlassCard className="space-y-4">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
            <Gauge className="h-4 w-4 text-indigo-400" /> Rate Limits
          </h3>
          <p className="mt-1 text-xs text-white/40">
            Applied per organisation before the proxy forwards upstream. Callers over the limit get a 429 with
            <code className="mx-1">Retry-After</code>. Leave a field blank to inherit the default.
          </p>
        </div>

        <div className="border-t border-white/5 pt-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/35">Defaults — every org without an override</p>
            {defaults && !defaultsDraft && (
              <GlassButton
                variant="secondary"
                size="sm"
                icon={<Pencil className="h-3 w-3" />}
                onClick={() => setDefaultsDraft({
                  requestsPerMinute: String(defaults.requestsPerMinute),
                  requestsPerHour: String(defaults.requestsPerHour),
                  tokensPerDay: String(defaults.tokensPerDay),
                  costPerDayUsd: String(defaults.costPerDayUsd),
                })}
              >
                Edit
              </GlassButton>
            )}
          </div>
          {defaultsDraft ? (
            <div className="grid items-end gap-2 md:grid-cols-5">
              <LimitField label="Requests / min" value={defaultsDraft.requestsPerMinute} onChange={(v) => setDefaultsDraft({ ...defaultsDraft, requestsPerMinute: v })} />
              <LimitField label="Requests / hour" value={defaultsDraft.requestsPerHour} onChange={(v) => setDefaultsDraft({ ...defaultsDraft, requestsPerHour: v })} />
              <LimitField label="Tokens / day" value={defaultsDraft.tokensPerDay} onChange={(v) => setDefaultsDraft({ ...defaultsDraft, tokensPerDay: v })} />
              <LimitField label="Cost / day (USD)" value={defaultsDraft.costPerDayUsd} onChange={(v) => setDefaultsDraft({ ...defaultsDraft, costPerDayUsd: v })} />
              <div className="flex gap-2">
                <GlassButton variant="primary" size="sm" onClick={() => void saveDefaults()}>Save</GlassButton>
                <GlassButton variant="secondary" size="sm" onClick={() => setDefaultsDraft(null)}>Cancel</GlassButton>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-4">
              <Metric label="Requests / min" value={defaults ? defaults.requestsPerMinute.toLocaleString() : '—'} />
              <Metric label="Requests / hour" value={defaults ? defaults.requestsPerHour.toLocaleString() : '—'} />
              <Metric label="Tokens / day" value={defaults ? defaults.tokensPerDay.toLocaleString() : '—'} />
              <Metric label="Cost / day (USD)" value={defaults ? `$${defaults.costPerDayUsd}` : '—'} />
            </div>
          )}
        </div>

        <div className="border-t border-white/5 pt-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/35">Per-org overrides</p>
          {overrides.length === 0 ? (
            <p className="text-xs text-white/40">No overrides — every org uses the defaults above.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wider text-white/35">
                <tr>
                  <th className="py-2 pr-4">Org</th>
                  <th className="py-2 pr-4">Product</th>
                  <th className="py-2 pr-4">Req/min</th>
                  <th className="py-2 pr-4">Req/hr</th>
                  <th className="py-2 pr-4">Tokens/day</th>
                  <th className="py-2 pr-4">Cost/day</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {overrides.map((row) => (
                  <tr key={`${row.orgId}:${row.productId ?? ''}`} className="border-t border-white/5">
                    <td className="py-2 pr-4 font-mono text-xs text-white/75">{row.orgId}</td>
                    <td className="py-2 pr-4 text-white/50">{row.productId ?? 'all'}</td>
                    <LimitCell value={row.requestsPerMinute} />
                    <LimitCell value={row.requestsPerHour} />
                    <LimitCell value={row.tokensPerDay} />
                    <LimitCell value={row.costPerDayUsd} money />
                    <td className="py-2">
                      <div className="flex gap-2">
                        <GlassButton
                          variant="secondary"
                          size="sm"
                          icon={<Pencil className="h-3 w-3" />}
                          onClick={() => editOverride(row)}
                        >
                          Edit
                        </GlassButton>
                        <GlassButton
                          variant="danger"
                          size="sm"
                          icon={<Trash2 className="h-3 w-3" />}
                          onClick={() => void removeOverride(row.orgId, row.productId)}
                        >
                          Remove
                        </GlassButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="grid gap-2 border-t border-white/5 pt-3 md:grid-cols-7">
          <LimitInput placeholder="Organisation ID" value={draft.orgId} onChange={(v) => setDraft({ ...draft, orgId: v })} className="md:col-span-2" />
          <LimitInput placeholder="Product (optional)" value={draft.productId} onChange={(v) => setDraft({ ...draft, productId: v })} />
          <LimitInput placeholder="Req/min" value={draft.requestsPerMinute} onChange={(v) => setDraft({ ...draft, requestsPerMinute: v })} />
          <LimitInput placeholder="Req/hr" value={draft.requestsPerHour} onChange={(v) => setDraft({ ...draft, requestsPerHour: v })} />
          <LimitInput placeholder="Tokens/day" value={draft.tokensPerDay} onChange={(v) => setDraft({ ...draft, tokensPerDay: v })} />
          <div className="flex gap-2">
            <LimitInput placeholder="Cost/day" value={draft.costPerDayUsd} onChange={(v) => setDraft({ ...draft, costPerDayUsd: v })} />
            <GlassButton variant="primary" size="sm" onClick={() => void saveOverride()}>Save</GlassButton>
          </div>
        </div>
      </GlassCard>

      <IssueKeyModal
        open={showCreate}
        draft={newKey}
        error={createError}
        creating={creating}
        onChange={setNewKey}
        onCreate={() => void createKey()}
        onCancel={() => setShowCreate(false)}
      />

      <ConfirmModal
        open={revokingId !== null}
        title="Revoke API Key"
        message="This action cannot be undone. The key will immediately stop working."
        confirmLabel="Revoke"
        variant="danger"
        loading={false}
        onConfirm={() => { if (revokingId) void revokeKey(revokingId); }}
        onCancel={() => setRevokingId(null)}
      />
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-white/35">{label}</p>
      <p className="text-lg text-white/85">{value}</p>
    </div>
  );
}

/** An override's value, or "default" where it inherits one. */
function LimitCell({ value, money = false }: { value: number | null; money?: boolean }) {
  return (
    <td className="py-2 pr-4 text-white/75">
      {value === null
        ? <span className="text-white/35">default</span>
        : money ? `$${value}` : value.toLocaleString()}
    </td>
  );
}

function LimitField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-white/35">{label}</span>
      <LimitInput placeholder={label} value={value} onChange={onChange} className="mt-1" />
    </label>
  );
}

function LimitInput({
  placeholder, value, onChange, className = '', type = 'text', autoFocus = false,
}: { placeholder: string; value: string; onChange: (value: string) => void; className?: string; type?: string; autoFocus?: boolean }) {
  return (
    <input
      type={type}
      autoFocus={autoFocus}
      className={`w-full rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-sm text-white/85 placeholder:text-white/25 focus:border-indigo-400/50 focus:outline-none ${className}`}
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/** The Issue Key form. The key's secret is shown on the page once it is made, not here. */
function IssueKeyModal({
  open, draft, error, creating, onChange, onCreate, onCancel,
}: {
  open: boolean;
  draft: NewKeyDraft;
  error: string | null;
  creating: boolean;
  onChange: (draft: NewKeyDraft) => void;
  onCreate: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && !creating) onCancel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, creating, onCancel]);

  if (!open) return null;
  const set = (field: keyof NewKeyDraft) => (value: string) => onChange({ ...draft, [field]: value });
  const ready = Boolean(draft.name.trim() && draft.userId.trim() && draft.orgId.trim() && draft.scopes.trim());

  return (
    <div className="fixed inset-0 z-[10500] flex items-center justify-center bg-black/65 p-4" onClick={() => { if (!creating) onCancel(); }}>
      <form
        className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#1a1a2e] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); if (ready && !creating) onCreate(); }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
              <Key className="h-4 w-4 text-indigo-400" /> Issue New API Key
            </h3>
            <p className="mt-1 text-xs text-white/50">Calls made with the key are made as this user and charged to this organisation.</p>
          </div>
          <button type="button" onClick={onCancel} disabled={creating} className="text-white/40 hover:text-white/60" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-5 space-y-3">
          <FormField label="Name" hint="What the key is for, e.g. the product that will hold it.">
            <LimitInput placeholder="Acme support bot" value={draft.name} onChange={set('name')} autoFocus />
          </FormField>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="User ID" hint="Filled in with yours.">
              <LimitInput placeholder="User ID" value={draft.userId} onChange={set('userId')} className="font-mono text-xs" />
            </FormField>
            <FormField label="Organisation ID" hint="Filled in with the one you have selected.">
              <LimitInput placeholder="Organisation ID" value={draft.orgId} onChange={set('orgId')} className="font-mono text-xs" />
            </FormField>
          </div>
          <FormField label="Scopes" hint="Comma-separated. chat:completions calls models, models:read lists them, model:<id> holds the key to one model.">
            <LimitInput placeholder={DEFAULT_SCOPES} value={draft.scopes} onChange={set('scopes')} className="font-mono text-xs" />
          </FormField>
          <FormField label="Expires (optional)" hint="Blank for a key that lasts until it is revoked.">
            <LimitInput type="date" placeholder="" value={draft.expires} onChange={set('expires')} />
          </FormField>
        </div>

        {error && (
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            <AlertCircle className="h-4 w-4 shrink-0" /> <span>{error}</span>
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <GlassButton type="button" variant="ghost" onClick={onCancel} disabled={creating}>Cancel</GlassButton>
          <GlassButton type="submit" variant="primary" loading={creating} disabled={!ready}>
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Create Key
          </GlassButton>
        </div>
      </form>
    </div>
  );
}

function FormField({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-white/35">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="mt-1 block text-xs text-white/35">{hint}</span>}
    </label>
  );
}
